import { Building } from "../../../building/Building";
import { RoofAddon } from "../../addon/RoofAddon";
import { GutterDownSpout } from "../gutter/GutterDownSpout";
import { Vector3, Mesh, Vector2, Object3D, MeshStandardMaterial, Texture } from "three";
import { Main } from "../../../Main";
import { BuildingGeometry } from "../../../building/BuildingGeometry";
import { THREEUtils } from "../../../utils/THREEUtils";
import { MaterialManager } from "../../../material/MaterialManager";
import { AddonManager } from "../../addon/AddonManager";
import { LeanToPost } from "../leanto/LeanToPost";
import { PostManager } from "../../../building/post/PostManager";

export class InsetBay
{
    private _whichWall : string;
    private _id : number;
    private _container : Object3D;
    private _building : Building;
    private _buildingConfig;
    private _insetBayConfig;

    private _roofRight : Mesh;
    private _roofLeft : Mesh;
	private _bottomRoofRight : Mesh; // bottom part of the roof -> the top part's color can be change, this one is always white
	private _bottomRoofLeft : Mesh;
    private _roofWidthLeft : number; // pithagoras -- the width of the roof
    private _roofWidthRight : number; // pithagoras -- the width of the roof
    private _zPosition : number;
    private _drop : number;

    private _angleRight : number;
    private _angleLeft : number;
    private _angleForSlopesRight : number;
    private _angleForSlopesLeft : number;
    private _topTrim : RoofAddon;
    private _slopeRight : Mesh;
    private _slopeLeft : Mesh;
    private _roofTrimRight : RoofAddon;
    private _roofTrimLeft : RoofAddon;
    private _cornerRight : Mesh;
    private _cornerLeft : Mesh;
    private _peakBox : RoofAddon;
    private _postRight : Mesh;
    private _postLeft : Mesh;
    private _innerPosts : Mesh[]; // If lean to on endwall of inset bay, add column to inset bay everywhere there is a column on lean to.

    private _gutterLeft  : Mesh;
    private _gutterRight : Mesh;
    private _gutterEnabled;
    private _downSpoutsLeft  : GutterDownSpout[];
    private _downSpoutsRight : GutterDownSpout[];

    private _leantoLeftDropIsNull  : boolean;
    private _leantoRightDropIsNull : boolean;

    private _isOkToShowCorners : boolean;

    private _areAssetsLoaded : boolean;

    constructor(whichWall: string, id: number, building: Building)
    {
        this._whichWall = whichWall;
        this._id = id;
        this._building = building;
        this._container = new Object3D();
        this._drop = 0;
        this._gutterEnabled = Main.getInstance().config.building.gutter;
        this._downSpoutsLeft  = [];
        this._downSpoutsRight = [];
        this._innerPosts = [];

        this._isOkToShowCorners = true;

        this._leantoLeftDropIsNull  = false;
        this._leantoRightDropIsNull = false;

        this._areAssetsLoaded = false;

        this.updateBuildingConfig();

        this._insetBayConfig = Main.getInstance().config.building.insetBay;

        this.initRoof();
        this.initListeners();
    }

    private initRoof()
    {
        if (this._whichWall === Building.WALLS.BACK)
        {
            this._container.rotateOnAxis(new Vector3(0, 1, 0), Math.PI);
        }

        var materials = this._building.materials;
        var roofGeometry = BuildingGeometry.createRoofGeometry();

        this._roofRight = new Mesh(roofGeometry, materials.roof_singleSided);
        this._roofRight.scale.z = this._id === 0 ? this._insetBayConfig.back.length_1.value : this._insetBayConfig.back.length_2.value; //0. index -> length_1
        this._roofRight.scale.y = this._roofWidthRight;
        this._roofRight.rotateOnAxis(new Vector3(0, 1, 0), Math.PI);
        this._roofRight.position.y = this._building.config.metrics.top + 0.001;
        this._roofRight.position.x = 0;
        this._roofRight.castShadow = true;

        this.updatePositions();

        this._roofRight.position.z = this._zPosition;
        THREEUtils.scaleUVs(this._roofRight.geometry, new Vector2(this._roofRight.scale.z, this._roofRight.scale.y));
        this._container.add(this._roofRight);

		this._bottomRoofRight = new Mesh(roofGeometry, materials.interior);
		this._bottomRoofRight.scale.z = this._roofRight.scale.z;
		this._bottomRoofRight.scale.y = this._roofRight.scale.y;
		this._bottomRoofRight.rotateOnAxis(new Vector3(0, 1, 0), Math.PI);
		this._bottomRoofRight.position.y = this._roofRight.position.y;
		this._bottomRoofRight.position.x = this._roofRight.position.x;
        this._bottomRoofRight.position.z = this._roofRight.position.z;
        THREEUtils.scaleUVs(this._bottomRoofRight.geometry, new Vector2(this._bottomRoofRight.scale.z, this._bottomRoofRight.scale.y));
        this._container.add(this._bottomRoofRight);

        this._roofLeft = new Mesh(roofGeometry, materials.roof_singleSided);
        this._roofLeft.scale.z = this._id === 0 ? this._insetBayConfig.back.length_1.value : this._insetBayConfig.back.length_2.value; //0. index -> length_1
        this._roofLeft.scale.y = this._roofRight.scale.y;
        this._angleLeft = Math.PI/2 + this._building.config.metrics.alpha;
        this._roofLeft.rotateOnAxis(new Vector3(0, 0, 1), this._angleLeft);
        this._roofLeft.position.y = this._roofRight.position.y;
        this._roofLeft.position.x = 0;
        this._roofLeft.position.z = this._roofRight.position.z;
        this._roofLeft.castShadow = true;
        THREEUtils.scaleUVs(this._roofLeft.geometry, new Vector2(this._roofLeft.scale.z, this._roofLeft.scale.y));
        this._container.add(this._roofLeft);

		this._bottomRoofLeft = new Mesh(roofGeometry, materials.interior);
		this._bottomRoofLeft.scale.z = this._roofLeft.scale.z;
		this._bottomRoofLeft.scale.y = this._roofLeft.scale.y;
		this._bottomRoofLeft.rotateOnAxis(new Vector3(0, 0, 1), this._angleLeft);
		this._bottomRoofLeft.position.y = this._roofLeft.position.y;
		this._bottomRoofLeft.position.x = this._roofLeft.position.x;
		this._bottomRoofLeft.position.z = this._roofLeft.position.z;
		THREEUtils.scaleUVs(this._bottomRoofLeft.geometry, new Vector2(this._bottomRoofLeft.scale.z, this._bottomRoofLeft.scale.y));
		this._container.add(this._bottomRoofLeft);

        this.initTopTrim();
        this.initSideTrims();
        this.initSlopesAndCornersAndGutters();
        this.initPeakBox();
        this.initPosts();
    }

    private initPosts()
    {
        var geometry = LeanToPost.getGeometry();

        var material = new MeshStandardMaterial({
            envMap: Main.getInstance().content.scene3D.background as Texture,
            roughness: 1,
            metalness: 0,
        });

        var materialColor = Main.getInstance().config.colorCollection.getByKey("rustic_red").hex;
        material.color.set(materialColor);


        this._postRight = new Mesh(geometry, material);
        this._postLeft = new Mesh(geometry, material);

        this._postRight.castShadow = true;
        this._postLeft.castShadow = true;

        this._container.add(this._postRight);
        this._container.add(this._postLeft);

        this.updatePosts();
    }

    private updatePosts()
    {
        var overhang = 0;
        var insetBays = Main.getInstance().config.building.insetBay;
        var insetBaysPresent = this._whichWall === Building.WALLS.FRONT ? insetBays.front.count.value : insetBays.back.count.value;

        var metrics = Main.getInstance().config.metrics;

        if (this._id === 0 && insetBaysPresent === 1 || this._id === 1 && insetBaysPresent === 2)
        {
            overhang = this._whichWall === Building.WALLS.FRONT ? metrics.overhang_front : metrics.overhang_back; // insetBays[this._whichWall].overhang.value;
        }
        this._postRight.scale.x = 1
        this._postRight.scale.z = 1;
        this._postRight.scale.y = this._buildingConfig.height - this._drop;
        this._postRight.position.x = this._building.config.metrics.width_half - 0.5;
        this._postRight.position.y = this._postRight.scale.y / 2;
        this._postRight.position.z = this._roofRight.position.z + this._roofRight.scale.z / 2 - overhang - 0.5;

        this._postLeft.scale.x = 1
        this._postLeft.scale.z = 1;
        this._postLeft.scale.y = this._postRight.scale.y;
        this._postLeft.position.x = -this._postRight.position.x;
        this._postLeft.position.y = this._postRight.position.y;
        this._postLeft.position.z = this._postRight.position.z;


        if (Main.getInstance().config.building.single_slope.value)
        {
            var offset = 0.2 + metrics.pitch / 12;
            if (this._whichWall === Building.WALLS.FRONT)
            {
                this._postRight.scale.y += metrics.width*metrics.pitch / 12 - offset;
                this._postRight.position.y = this._postRight.scale.y / 2;
            }
            else
            {
                this._postLeft.scale.y += metrics.width*metrics.pitch / 12 - offset;
                this._postLeft.position.y = this._postLeft.scale.y / 2;
            }
        }



        for (var i = 0; i < this._innerPosts.length; ++i)
        {
            this._container.remove(this._innerPosts[i]);
        }
        this._innerPosts.length = 0;

        var insetBays = Main.getInstance().config.building.insetBay;
        var insetBaysPresent = this._whichWall === Building.WALLS.FRONT ? insetBays.front.count.value : insetBays.back.count.value;

        if (this._id === 0 && insetBaysPresent === 1 || this._id === 1 && insetBaysPresent === 2)
        {
            var leanto = Main.getInstance().config.building.leanto;
            var isLeantoPresent = this._whichWall === Building.WALLS.FRONT ? leanto.front.enabled.value : leanto.back.enabled.value;

            if (isLeantoPresent)
            {
                var geometry = LeanToPost.getGeometry();
                var material = new MeshStandardMaterial({
                    envMap: Main.getInstance().content.scene3D.background as Texture,
                    roughness: 1,
                    metalness: 0,
                });
                var materialColor = Main.getInstance().config.colorCollection.getByKey("rustic_red").hex;
                material.color.set(materialColor);

                var numberOfBays = Math.ceil(this._buildingConfig.width / PostManager.IntervalOnEnd);
                var requiredPosts = numberOfBays + 1;

                for (var i = 1; i < requiredPosts - 1; ++i)
                {
                    var length = this._buildingConfig.width;
                    var x = -length/2 + i / (requiredPosts-1) * length;
                    var height = (this._buildingConfig.height + (this._buildingConfig.pitch/12)*(length/2 - Math.abs(x))) - this._drop - 0.2;
                    var z = this._postLeft.position.z;

                    var post = new Mesh(geometry, material);
                    post.scale.x = 1;
                    post.scale.z = 1;
                    post.scale.y = height;
                    post.position.set(x, height/2, z);
                    post.castShadow = true;

                    if (Main.getInstance().config.building.single_slope.value)
                    {
                        var negativeX = true;
                        if (this._whichWall === Building.WALLS.FRONT)
                        {
                            negativeX = false;
                        }

                        if ((post.position.x > 0 && !negativeX) || (post.position.x < 0 && negativeX))
                        {
                            post.scale.y += Math.abs(post.position.x)*metrics.pitch / 6;
                            post.position.y = post.scale.y / 2;
                        }
                    }


                    this._innerPosts.push(post);
                }

                for (var i = 0; i < this._innerPosts.length; ++i)
                {
                    this._container.add(this._innerPosts[i]);
                }
            }
        }
    }

    private initSideTrims()
    {
        var config = {
            "id": "",
            "label": "Low Profile Vent",
            "mesh": {
                "1": "#0810 - Sculptured Eave Trim - 1/12 Pitch",
                "2": "#0810 - Sculptured Eave Trim - 2/12 Pitch",
                "3": "#0810 - Sculptured Eave Trim - 3/12 Pitch",
                "4": "#0810 - Sculptured Eave Trim - 4/12 Pitch",
                "5": "#0810 - Sculptured Eave Trim - 4/12 Pitch"
            },
            "size": {
                "length": 0
            },
            "material": MaterialManager.getInstance().trim,
            "pitch": Main.getInstance().config.building.pitch
        };

        this._roofTrimRight = new RoofAddon(config);

        this._roofTrimRight.addToScene(this._container);
        this._roofTrimRight.show();

        this._roofTrimLeft = new RoofAddon(config);

        this._roofTrimLeft.container.rotation.y = Math.PI;
        this._roofTrimLeft.addToScene(this._container);
        this._roofTrimLeft.show();


        this.updateSideTrims();
    }

    private updateSideTrims()
    {
        var metrics = this._building.config.metrics;

        var overhangRight = this._whichWall === Building.WALLS.FRONT ? metrics.overhang_right : metrics.overhang_left;
        var overhangLeft  = this._whichWall === Building.WALLS.FRONT ? metrics.overhang_left  : metrics.overhang_right;

        var x_left  = -metrics.width_half - overhangLeft;
        var x_right =  metrics.width_half + overhangRight;
        var y_left  =  metrics.height - overhangLeft  *  Math.sin(metrics.alpha);
        var y_right =  metrics.height - overhangRight *  Math.sin(metrics.alpha);

        var side_offset = 0.45;

        var side_offset_x = side_offset * Math.cos(metrics.alpha);
        var side_offset_y = side_offset * Math.sin(metrics.alpha);
        //
        //RIGHT
        //
        var trimContainer = this._roofTrimRight.container;
        trimContainer.position.z = this._roofRight.position.z + this._roofRight.scale.z / 2;
        trimContainer.position.x = x_right + side_offset_x;
        trimContainer.position.y = y_right - side_offset_y - this._drop;

        // 200 is the scale of the children meshes (set in blender)
        trimContainer.scale.z = this._roofRight.scale.z / 200;

        //
        //LEFT
        //
        trimContainer = this._roofTrimLeft.container;
        trimContainer.position.z = this._roofRight.position.z - this._roofRight.scale.z / 2;
        trimContainer.position.x = x_left - side_offset_x;
        trimContainer.position.y = y_left - side_offset_y - this._drop;

        // 200 is the scale of the children meshes (set in blender)
        trimContainer.scale.z = this._roofRight.scale.z / 200;


        if (Main.getInstance().config.building.single_slope.value)
        {
            var trimContainer: Object3D;
            var alpha = Math.atan(-this._buildingConfig.pitch/12);
            var offset_y = side_offset * Math.sin(alpha);
            var newY = (metrics.height - metrics.overhang_right * Math.sin(metrics.alpha) + 2*((metrics.width_half + metrics.overhang_right) * metrics.tan)) - offset_y;
            if (this._whichWall === Building.WALLS.FRONT)
            {
                trimContainer = this._roofTrimRight.container;
                this._roofTrimRight.updatePitch(undefined, -this._buildingConfig.pitch);
                trimContainer.position.y = newY;
            }
            else
            {
                trimContainer = this._roofTrimLeft.container;
                this._roofTrimLeft.updatePitch(undefined, -this._buildingConfig.pitch);
                trimContainer.position.y = newY;
            }
        }

        this._leantoRightDropIsNull = false;
        this._leantoLeftDropIsNull  = false;

        if (Main.getInstance().config.building.leanto.right.enabled.value === true)
        {
            if (Main.getInstance().config.building.leanto.right.drop.value === 0 && !Main.getInstance().config.building.single_slope.value)
            {
                this._leantoRightDropIsNull = true;
                if (this._whichWall === Building.WALLS.FRONT)
                {
                    this._roofTrimRight.hide();
                }
                else
                {
                    this._roofTrimLeft.hide();
                }
            }
        }
        if (Main.getInstance().config.building.leanto.left.enabled.value === true)
        {
            if (Main.getInstance().config.building.leanto.left.drop.value === 0)
            {
                this._leantoLeftDropIsNull = true;
                if (this._whichWall === Building.WALLS.FRONT)
                {
                    this._roofTrimLeft.hide();
                }
                else
                {
                    this._roofTrimRight.hide();
                }
            }
        }
    }

    private initPeakBox()
    {
        var peakBoxConfig = {
            "id": "",
            "label": "Low Profile Vent",
            "mesh": {
                "1": "#0940 - Peak Box for 1440 Rake - 1/12 Pitch",
                "2": "#0940 - Peak Box for 1440 Rake - 2/12 Pitch",
                "3": "#0940 - Peak Box for 1440 Rake - 3/12 Pitch",
                "4": "#0940 - Peak Box for 1440 Rake - 4/12 Pitch",
                "5": "#0940 - Peak Box for 1440 Rake - 4/12 Pitch"
            },
            "type": "roof",
            "size": {
                "length": 0
            },
            "material": MaterialManager.getInstance().trim,
            "pitch": this._building.config.building.pitch
        };

        this._peakBox = new RoofAddon(peakBoxConfig);
        this._peakBox.addToScene(this._container);
        this._peakBox.show();
        this.updatePeakBox();
    }

    private initTopTrim()
    {
        var eaveTrimConfig = {
            "id": "",
            "label": "",
            "mesh": {
                "1": "#0200 - Ridge Roll - 1/12 Pitch",
                "2": "#0200 - Ridge Roll - 2/12 Pitch",
                "3": "#0200 - Ridge Roll - 3/12 Pitch",
                "4": "#0200 - Ridge Roll - 4/12 Pitch",
                "5": "#0200 - Ridge Roll - 4/12 Pitch",
            },
            "type": "roof",
            "size": {
                "length": 0
            },
            "material": MaterialManager.getInstance().trim_roofColor,
            "pitch": Main.getInstance().config.building.pitch
        };

        this._topTrim = new RoofAddon(eaveTrimConfig);

        this._topTrim.addToScene(this._container);
        this._topTrim.show();
        this.updateTopTrim();
    }
    private updateTopTrim()
    {
        if (Main.getInstance().config.building.single_slope.value)
        {
            this._topTrim.hide();

            return;
        }
        else
        {
            this._topTrim.show();
        }
        var trimContainer = this._topTrim.container;
        trimContainer.position.z = this._roofLeft.position.z;
        trimContainer.position.x = 0;
        trimContainer.position.y = this._building.config.metrics.top + 0.04 - this._drop;

        // 200 is the scale of the children meshes (set in blender)
        trimContainer.scale.z = this._roofRight.scale.z / 200;
    }

    private initSlopesAndCornersAndGutters()
    {
        this._areAssetsLoaded = true;
        var meshName = "#1440 - Sculptured Rake Trim - scalable";
        var material = MaterialManager.getInstance().trim;
        this._slopeRight = AddonManager.getInstance().createMesh(meshName, material) as Mesh;
        this._slopeRight.castShadow = true;
        this._container.add(this._slopeRight);

        this._slopeLeft = AddonManager.getInstance().createMesh(meshName + "_flipped", material) as Mesh;
        this._slopeLeft.castShadow = true;
        this._container.add(this._slopeLeft);

        this._angleForSlopesRight =  this._building.config.metrics.alpha;
        this._angleForSlopesLeft =  -this._angleForSlopesRight;
        this._slopeLeft.rotation.z  = 0;

        this._slopeRight.rotation.y = this._angleForSlopesRight;
        this._slopeLeft.rotation.y  = this._angleForSlopesLeft;


        this.updateSlopes();

        this._cornerLeft = AddonManager.getInstance().createMesh("Corner Box - Generic_flipped", material) as Mesh;
        this._cornerRight = AddonManager.getInstance().createMesh("Corner Box - Generic", material) as Mesh;

        this._cornerLeft.rotateOnAxis(new Vector3(0, 0, 1), Math.PI);

        this._cornerLeft.castShadow = true;
        this._cornerRight.castShadow = true;
        this._container.add(this._cornerLeft);
        this._container.add(this._cornerRight);

        this.updateCorners();

        this._gutterLeft = AddonManager.getInstance().createMesh("#0503 - Utility Gutter", material) as Mesh;
        this._gutterRight = AddonManager.getInstance().createMesh("#0503 - Utility Gutter", material) as Mesh;

        this._gutterLeft.castShadow = true;
        this._gutterRight.castShadow = true;

        this.updateGutters();
    }

    private updateCorners = () =>
    {
        if (!this._cornerRight || !this._cornerLeft)
        {
            return;
        }
        this._cornerRight.position.x = this._roofTrimRight.container.position.x;
        this._cornerRight.position.y = this._roofTrimRight.container.position.y;
        this._cornerRight.position.z = this._roofRight.position.z + this._roofRight.scale.z/2;
        this._cornerRight.rotation.y = Math.atan(this._buildingConfig.pitch / 12);

        this._cornerLeft.position.x = this._roofTrimLeft.container.position.x;
        this._cornerLeft.position.y = this._roofTrimLeft.container.position.y;
        this._cornerLeft.position.z = this._roofLeft.position.z + this._roofLeft.scale.z/2;
        this._cornerLeft.rotation.y = -Math.atan(this._buildingConfig.pitch / 12);

        if (Main.getInstance().config.building.single_slope.value)
        {
            if (this._whichWall === Building.WALLS.FRONT)
            {
                this._cornerRight.rotation.y = this._cornerLeft.rotation.y;
            }
            else
            {
                this._cornerLeft.rotation.y = this._cornerRight.rotation.y;
            }
        }

        if (this._isOkToShowCorners)
        {
            this.showElement(this._cornerLeft);
            this.showElement(this._cornerRight);
        }
        else
        {
            this.hideElement(this._cornerLeft);
            this.hideElement(this._cornerRight);
        }

        var corner = this._whichWall === Building.WALLS.FRONT ? this._cornerRight : this._cornerLeft;
        if (Main.getInstance().config.building.leanto.right.enabled.value === true)
        {
            if (Main.getInstance().config.building.leanto.right.drop.value === 0 && !Main.getInstance().config.building.single_slope.value)
            {
                this.hideElement(corner);
            }
        }
        corner = this._whichWall === Building.WALLS.FRONT ? this._cornerLeft : this._cornerRight;
        if (Main.getInstance().config.building.leanto.left.enabled.value === true)
        {
            if (Main.getInstance().config.building.leanto.left.drop.value === 0)
            {
                this.hideElement(corner);
            }
        }
    }

    private updateSlopes = () =>
    {
        if (!this._slopeRight || !this._slopeLeft)
        {
            return;
        }
        var metrics = this._building.config.metrics;

        var overhangRight = this._whichWall === Building.WALLS.FRONT ? metrics.overhang_right : metrics.overhang_left;
        var overhangLeft  = this._whichWall === Building.WALLS.FRONT ? metrics.overhang_left  : metrics.overhang_right;

        var x_left  = -metrics.width_half - overhangLeft;
        var x_right =  metrics.width_half + overhangRight;
        var y_left  =  metrics.height - overhangLeft  *  Math.sin(metrics.alpha) - this._drop;
        var y_right =  metrics.height - overhangRight *  Math.sin(metrics.alpha) - this._drop;

        var side_offset = 0.1;

        var side_offset_x = side_offset * Math.cos(metrics.alpha);
        var side_offset_y = side_offset * Math.sin(metrics.alpha);

        this._slopeRight.scale.x = this._roofRight.scale.y*AddonManager.DEFAULT_SCALE;
        this._slopeRight.position.y = y_right + side_offset_y;
        this._slopeRight.position.z = this._zPosition + this._roofRight.scale.z / 2;
        this._slopeRight.position.x = x_right - side_offset_x;

        this._slopeLeft.scale.x = this._roofLeft.scale.y*AddonManager.DEFAULT_SCALE;
        this._slopeLeft.position.y = y_left - side_offset_y;
        this._slopeLeft.position.z = this._zPosition + this._roofLeft.scale.z / 2;
        this._slopeLeft.position.x = x_left - side_offset_x;

        this._angleForSlopesRight = metrics.alpha;
        this._angleForSlopesLeft  = -metrics.alpha;


        if (Main.getInstance().config.building.single_slope.value)
        {
            if (this._whichWall === Building.WALLS.FRONT)
            {
                this.hideElement(this._slopeRight);
                this._slopeLeft.scale.x += this._slopeRight.scale.x;
            }
            else
            {
                this.hideElement(this._slopeLeft);
                this._slopeRight.scale.x += this._slopeLeft.scale.x;
            }
        }


        this._slopeRight.rotation.y = this._angleForSlopesRight;
        this._slopeLeft.rotation.y = this._angleForSlopesLeft;
    }

    private updatePeakBox = () =>
    {
        if (!this._areAssetsLoaded)
        {
            return;
        }

        var singleSlope = Main.getInstance().config.building.single_slope.value;
        if (singleSlope)
        {
            this._peakBox.hide();
        }
        else
        {
            this._peakBox.show();
        }

        var metrics = this._building.config.metrics;
        var offset = 0.26 + Math.max(metrics.overhang_left, metrics.overhang_right)/100 + metrics.pitch/100;
        var container = this._peakBox.container;
        container.position.z = this._roofLeft.position.z + this._roofLeft.scale.z / 2 + 0.22;
        container.position.x = 0;
        container.position.y = this._building.config.metrics.top + offset - this._drop;

        var insetBay = this._whichWall === Building.WALLS.FRONT ? this._insetBayConfig.front : this._insetBayConfig.back;

        if (this._id === 0 && insetBay.count.value > 1)
        {
            this._peakBox.hide();
            this.hideElement(this._slopeLeft);
            this.hideElement(this._slopeRight);
            this._isOkToShowCorners = false;
        }
        else
        {
            if (!singleSlope)
            {
                this._peakBox.show();
            }
            this.showElement(this._slopeLeft);
            this.showElement(this._slopeRight);
            this._isOkToShowCorners = true;
        }
    }



    private initListeners()
    {
        Main.getInstance().config.building.width.signal.add(this.update);
        Main.getInstance().config.building.height.signal.add(this.update);
        Main.getInstance().config.building.length.signal.add(this.update);
        Main.getInstance().config.building.pitch.signal.add(this.update);
    }

    public changeDropTo(val: number)
    {
        this._drop = val;
        this._roofRight.position.y       = this._building.config.metrics.top - val + 0.001;
		this._bottomRoofRight.position.y = this._building.config.metrics.top - val + 0.001;
        this._roofLeft.position.y        = this._building.config.metrics.top - val + 0.001;
		this._bottomRoofLeft.position.y  = this._building.config.metrics.top - val + 0.001;

        this.update();
    }

    public changeLengthTo(val: number)
    {
        this._roofRight.scale.z = val;
        this._roofRight.position.z = this._zPosition;
        THREEUtils.scaleUVs(this._roofRight.geometry, new Vector2(this._roofRight.scale.z, this._roofRight.scale.y));
		this._bottomRoofRight.scale.z = this._roofRight.scale.z;
		this._bottomRoofRight.position.z = this._roofRight.position.z;
		THREEUtils.scaleUVs(this._bottomRoofRight.geometry, new Vector2(this._bottomRoofRight.scale.z, this._bottomRoofRight.scale.y));

        this._roofLeft.scale.z = val;
        this._roofLeft.position.z = this._roofRight.position.z;
        THREEUtils.scaleUVs(this._roofLeft.geometry, new Vector2(this._roofLeft.scale.z, this._roofLeft.scale.y));
		this._bottomRoofLeft.scale.z = this._roofLeft.scale.z;
		this._bottomRoofLeft.position.z = this._roofLeft.position.z;
		THREEUtils.scaleUVs(this._bottomRoofLeft.geometry, new Vector2(this._bottomRoofLeft.scale.z, this._bottomRoofLeft.scale.y));

        this.update();
    }

    public show()
    {
        this._building.scene3D.add(this._container);
        this.update();
    }

    public hide()
    {
        this._building.scene3D.remove(this._container);
        this.update();
    }

    private hideElement(element: Mesh)
    {
        this._container.remove(element);
    }
    private showElement(element: Mesh)
    {
        this._container.add(element);
    }

    public update = () =>
    {
        this.updatePositions();
        this.updateRoof();

        this.updateTopTrim();
        this.updatePeakBox();
        this.updateSideTrims();
        this.updateSlopes();
        this.updateCorners();
        this.updatePosts();
        this.updateGutters();

        Main.getInstance().render = true;
    }

    private updateGutters = () =>
    {
        if (!this._gutterLeft || !this._gutterRight)
        {
            return;
        }

        var metrics = Main.getInstance().config.metrics;
        var topLeft  = this._whichWall === Building.WALLS.FRONT ? metrics.height - metrics.overhang_left  *  Math.sin(metrics.alpha) : metrics.height - metrics.overhang_right  *  Math.sin(metrics.alpha);
        var topRight = this._whichWall === Building.WALLS.BACK  ? metrics.height - metrics.overhang_left  *  Math.sin(metrics.alpha) : metrics.height - metrics.overhang_right  *  Math.sin(metrics.alpha);

        var side_offset = 0.45;

        var alpha = Math.atan(metrics.pitch/12);

        var side_offset_y = side_offset * Math.sin(alpha);

        this._gutterLeft.scale.y = this._roofLeft.scale.z / 200 * AddonManager.DEFAULT_SCALE;
        this._gutterLeft.rotation.z = Math.PI;
        this._gutterLeft.position.y = topLeft - side_offset_y;
        this._gutterLeft.position.x = this._roofTrimLeft.container.position.x;
        this._gutterLeft.position.z = this._roofLeft.position.z - this._roofLeft.scale.z / 2;

        this._gutterRight.scale.y = this._roofRight.scale.z / 200 * AddonManager.DEFAULT_SCALE;
        this._gutterRight.position.y = topRight - side_offset_y;
        this._gutterRight.position.x = this._roofTrimRight.container.position.x;
        this._gutterRight.position.z = this._roofRight.position.z + this._roofRight.scale.z / 2;




        for (var i = 0; i < this._downSpoutsLeft.length; ++i)
        {
            this._container.remove(this._downSpoutsLeft[i].container);
        }
        this._downSpoutsLeft.length = 0;
        for (var i = 0; i < this._downSpoutsRight.length; ++i)
        {
            this._container.remove(this._downSpoutsRight[i].container);
        }
        this._downSpoutsRight.length = 0;

        if (!this._gutterEnabled.value)
        {
            this.hideElement(this._gutterLeft);
            this.hideElement(this._gutterRight);

            var roofTrim = this._whichWall === Building.WALLS.FRONT ? this._roofTrimLeft : this._roofTrimRight;
            if (this._leantoLeftDropIsNull)
            {
                roofTrim.hide();
            }
            else
            {
                roofTrim.show();
            }

            roofTrim = this._whichWall === Building.WALLS.FRONT ? this._roofTrimRight : this._roofTrimLeft;
            if (this._leantoRightDropIsNull && !Main.getInstance().config.building.single_slope.value)
            {
                roofTrim.hide();
            }
            else
            {
                roofTrim.show();
            }

            return;
        }
        this.showElement(this._gutterLeft);
        this.showElement(this._gutterRight);
        this._roofTrimLeft.hide();
        this._roofTrimRight.hide();

        //var spacing = GutterDownSpout.SPACING;
        //var requiredSpouts = Math.max(0, Math.ceil(this._roofRight.scale.z / spacing) - 2) + 2;

        var baseOffset = 0.1;
        var wallOffset_left  = this._whichWall === Building.WALLS.FRONT ? metrics.overhang_left - baseOffset : metrics.overhang_right - baseOffset;
        var wallOffset_right = this._whichWall === Building.WALLS.BACK  ? metrics.overhang_left - baseOffset : metrics.overhang_right - baseOffset;

        var width_left   = metrics.width_half + metrics.overhang_left;
        var width_right  = metrics.width_half + metrics.overhang_right;

        var leftDownSpout = new GutterDownSpout();


        var pos = new Vector3();

        var zOffset = 0.2;

        pos.x = this._whichWall === Building.WALLS.FRONT ? -width_left : -width_right;
        pos.y = this._gutterLeft.position.y;
        pos.z = this._postLeft.position.z + zOffset;


        leftDownSpout.update(pos, 0.85, wallOffset_left);

        this._downSpoutsLeft.push(leftDownSpout);



        pos.x = this._whichWall === Building.WALLS.BACK ? -width_left : -width_right;
        pos.y = this._gutterRight.position.y;
        pos.z = -pos.z + 2*zOffset;

        var rightDownSpout = new GutterDownSpout();
        rightDownSpout.container.rotateOnAxis(new Vector3(0, 1, 0), Math.PI);
        rightDownSpout.update(pos, 0.85, wallOffset_right);
        this._downSpoutsRight.push(rightDownSpout);

        for (var i = 0; i < this._downSpoutsLeft.length; ++i)
        {
            this._container.add(this._downSpoutsLeft[i].container);
        }
        for (var i = 0; i < this._downSpoutsRight.length; ++i)
        {
            this._container.add(this._downSpoutsRight[i].container);
        }


        var roofTrim   = this._whichWall === Building.WALLS.BACK ? this._roofTrimLeft   : this._roofTrimRight;
        var gutter     = this._whichWall === Building.WALLS.BACK ? this._gutterLeft     : this._gutterRight;
        var downSpouts = this._whichWall === Building.WALLS.BACK ? this._downSpoutsLeft : this._downSpoutsRight;

        if (Main.getInstance().config.building.leanto.right.enabled.value || Main.getInstance().config.building.single_slope.value)
        {
            if (this._leantoRightDropIsNull)
            {
                roofTrim.hide();
            }
            else
            {
                roofTrim.show();
            }
            this.hideElement(gutter);
            for (var i = 0; i < downSpouts.length; ++i)
            {
                downSpouts[i].setEnabled(false);
            }
        }

        roofTrim   = this._whichWall === Building.WALLS.FRONT ? this._roofTrimLeft   : this._roofTrimRight;
        gutter     = this._whichWall === Building.WALLS.FRONT ? this._gutterLeft     : this._gutterRight;
        downSpouts = this._whichWall === Building.WALLS.FRONT ? this._downSpoutsLeft : this._downSpoutsRight;
        if (Main.getInstance().config.building.leanto.left.enabled.value)
        {
            if (this._leantoLeftDropIsNull)
            {
                roofTrim.hide();
            }
            else
            {
                roofTrim.show();
            }
            this.hideElement(gutter);
            for (var i = 0; i < downSpouts.length; ++i)
            {
                downSpouts[i].setEnabled(false);
            }
        }
    };

    private updateRoof = () =>
    {
        this._roofRight.position.z = this._zPosition;
		this._bottomRoofRight.scale.z = this._roofRight.scale.z;
		this._bottomRoofRight.position.z = this._roofRight.position.z;

        this._roofLeft.position.z = this._roofRight.position.z;
		this._bottomRoofLeft.position.z = this._roofLeft.position.z;

        this._roofRight.position.y = this._building.config.metrics.top - this._drop + 0.001;
		this._bottomRoofRight.position.y = this._roofRight.position.y;

        this._roofLeft.position.y = this._roofRight.position.y;
        this._roofLeft.scale.z = this._roofRight.scale.z;
		this._bottomRoofLeft.position.y = this._roofLeft.position.y;
		this._bottomRoofLeft.scale.z = this._roofRight.scale.z;

        this._roofRight.scale.y = this._roofWidthRight;
		this._bottomRoofRight.scale.y = this._roofRight.scale.y;
        this._roofLeft.scale.y = this._roofWidthLeft;
		this._bottomRoofLeft.scale.y = this._roofLeft.scale.y;

        this._angleLeft  = Math.PI/2 + this._building.config.metrics.alpha;
        this._angleRight = -Math.PI/2 + this._building.config.metrics.alpha;

        if (this._whichWall === Building.WALLS.FRONT)
        {
            if (Main.getInstance().config.building.single_slope.value)
            {
                this._angleRight = - this._angleLeft;
            }
        }
        else
        {
            if (Main.getInstance().config.building.single_slope.value)
            {
                this._angleLeft = - this._angleRight;
            }
        }

        this._roofRight.rotation.z = this._angleRight;
		this._bottomRoofRight.rotation.z = this._roofRight.rotation.z;

        this._roofLeft.rotation.z = this._angleLeft;
		this._bottomRoofLeft.rotation.z = this._roofLeft.rotation.z;

        THREEUtils.scaleUVs(this._roofRight.geometry, new Vector2(this._roofRight.scale.z, this._roofRight.scale.y));
        THREEUtils.scaleUVs(this._bottomRoofRight.geometry, new Vector2(this._bottomRoofRight.scale.z, this._bottomRoofRight.scale.y));
		THREEUtils.scaleUVs(this._roofLeft.geometry, new Vector2(this._roofLeft.scale.z, this._roofLeft.scale.y));
        THREEUtils.scaleUVs(this._bottomRoofLeft.geometry, new Vector2(this._bottomRoofLeft.scale.z, this._bottomRoofLeft.scale.y));
    };

    private updatePositions()
    {
        this.updateBuildingConfig();
        var metrics = this._building.config.metrics;
        var alpha = metrics.alpha;
        var overhangRight = this._whichWall === Building.WALLS.FRONT ? metrics.overhang_right : metrics.overhang_left;
        var overhangLeft  = this._whichWall === Building.WALLS.FRONT ? metrics.overhang_left  : metrics.overhang_right;
        this._roofWidthRight = (metrics.width_half + overhangRight) / Math.cos(alpha);
        this._roofWidthLeft  = (metrics.width_half + overhangLeft) / Math.cos(alpha);

        var overhang = 0;
        var id = this._id + 1;
        var length = Main.getInstance().config.building.insetBay[this._whichWall]["length_" + id].value;

        var insetBays = Main.getInstance().config.building.insetBay;
        var insetBaysPresent = this._whichWall === Building.WALLS.FRONT ? insetBays.front.count.value : insetBays.back.count.value;

        if (this._id === 0 && insetBaysPresent === 1 || this._id === 1 && insetBaysPresent === 2)
        {
            overhang = this._whichWall === Building.WALLS.FRONT ? metrics.overhang_front : metrics.overhang_back;
        }
        this._roofRight.scale.z = length + overhang;

        var otherId = this._id === 0 ? 2 : 1;
        var otherLength = Main.getInstance().config.building.insetBay[this._whichWall]["length_" + otherId].value;

        if (this._id === 0)
        {
            this._zPosition = this._buildingConfig.length / 2 + this._roofRight.scale.z / 2;
        }
        else
        {
            this._zPosition = this._buildingConfig.length / 2 + otherLength + this._roofRight.scale.z / 2;
        }
    }

    private updateBuildingConfig()
    {
        this._buildingConfig = {
            width: Main.getInstance().config.building.width.value,
            length: Main.getInstance().config.building.length.value,
            height: Main.getInstance().config.building.height.value,
            pitch: Main.getInstance().config.building.pitch.value
        };
    }
}